import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Lock, ChevronLeft, ChevronRight, Copy, Check as CheckIcon } from "lucide-react";
export { formatNullableMoney, isCommissionPayoutEligible } from "./commission-utils";

export function CopyableAccountNumber({ accountNumber }: { accountNumber: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(accountNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [accountNumber]);
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 font-mono text-xs bg-muted/60 hover:bg-muted border border-border rounded px-1.5 py-0.5 transition-colors text-foreground"
            data-testid={`copyable-account-number-${accountNumber}`}
          >
            {accountNumber}
            {copied
              ? <CheckIcon className="w-3 h-3 text-green-500 shrink-0" />
              : <Copy className="w-3 h-3 text-muted-foreground shrink-0" />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {copied ? "Copied!" : "Click to copy"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export type AdminSection =
  | "command-center"
  | "dealers"
  | "salespeople"
  | "sales-teams"
  | "company-performance"
  | "commissions"
  | "ai-insights"
  | "ai-intelligence"
  | "pricing"
  | "messaging"
  | "rbac"
  | "vendor-directory"
  | "feedback"
  | "dealer-feed"
  | "codes"
  | "upload-diagnostics"
  | "api-docs"
  | "env-health"
  | "dealer-analytics"
  | "telemetry"
  | "database-training";

export interface AdminUser {
  id: string;
  username: string;
  name?: string | null;
  email: string | null;
  companyName: string | null;
  role: string;
  subscriptionStatus: string | null;
  onboardedById: string | null;
  onboardDate: string | null;
  createdAt: string | null;
  commissionRate: string | null;
  referralCode: string | null;
  commissionType: string | null;
  commissionStructure: string | null;
  commissionInitialRate: string | null;
  commissionResidualRate: string | null;
  commissionResidualMonths: number | null;
  payFrequency: string | null;
  goalType: string | null;
  goalValue: string | null;
  canceledAt: string | null;
  accountNumber: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  accountStatus: string | null;
  trialEndsAt?: string | null;
  trialSource?: string | null;
  codeUsed?: string | null;
  region: string | null;
  phone: string | null;
  teamId: string | null;
  salespersonRoleId: string | null;
  isSuperAdmin?: boolean;
  // Sales Leader fields
  isLeader?: boolean;
  leaderCommissionType?: string | null;
  leaderCommissionRate?: string | null;
  leaderCommissionStructure?: string | null;
  leaderCommissionInitialRate?: string | null;
  leaderCommissionResidualRate?: string | null;
  leaderCommissionResidualMonths?: number | null;
  leaderGoalType?: string | null;
  leaderGoalValue?: string | null;
  leaderPayFrequency?: string | null;
  streakMonths?: number | null;
  termsAcceptedAt?: string | null;
  tosVersion?: string | null;
}

export interface AuditEvent {
  id: string;
  repId: string;
  changedBy: string;
  changedByUsername: string;
  eventType: string;
  fieldChanged: string | null;
  fromValue: string | null;
  toValue: string | null;
  reason: string | null;
  createdAt: string | null;
}

export interface TaxInfo {
  taxId: string | null;
  mailingAddress: string | null;
  contractStart: string | null;
  contractEnd: string | null;
}

export interface CommissionRow {
  id: string;
  username: string;
  name?: string | null;
  email: string | null;
  totalDealers: number;
  activeDealers: number;
  grossMrr: number | null;
  netMrr: number | null;
  commissionMarginPct: number | null;
  /** Whether Stripe supplied the revenue facts used for this row. */
  mrrSource?: "stripe" | "unavailable" | null;
  source?: "stripe" | "unavailable" | null;
  commissionRate: string | null;
  commissionType: string | null;
  commissionStructure: string | null;
  referralCode: string | null;
  commissionDue: number | null;
  commissionFormula: string;
  dealerBreakdown: {
    dealerId: string;
    dealerName: string;
    accountNumber: string | null;
    status: string;
    monthlyValue: number;
    rateApplied: number;
    amountEarned: number;
    monthsElapsed: number;
    residualMonthsRemaining: number | null;
    isActive: boolean;
  }[];
  payFrequency: string | null;
  goalType: string | null;
  goalValue: string | null;
  commissionProtected?: boolean;
  accountStatus?: string;
}


export interface AnalyticsData {
  summary: {
    topPages: { page: string; count: number }[];
    eventTypeAggregates: { eventType: string; count: number }[];
    uploadsOverTime: { date: string; count: number }[];
    totalEvents: number;
    recentDays: number;
  };
  dealerActivity: { id: string; username: string; companyName: string | null; subscriptionStatus: string | null; eventCount: number; uploadCount: number; lastActive: string | null }[];
  mrr: { amount: number | null; activeSubscriptions: number | null; source: "stripe" | "unavailable"; computedAt: string };
  churn: { cancellationRate: number; monthlyChurnRate: number; canceledThisMonth: number; canceledDealers: { id: string; username: string; companyName: string | null; canceledAt: string | null }[]; mrrLost: number | null; activeDealers: number; totalDealers: number };
}

export interface AIInsight {
  urgency: "High" | "Medium" | "Low";
  category: "retention" | "adoption" | "sales" | "operations";
  title: string;
  description: string;
  navigationTarget?: AdminSection;
  navigationFilter?: string;
}

export interface AIInsightsData {
  insights: AIInsight[];
  generatedAt: string;
  cached: boolean;
}

export interface QueueHealthData {
  lockRenewals: Record<string, { renewalsLastHour: number; windowMs: number }>;
  lockRenewalHistory: Record<string, number[]>;
  redisAvailable: boolean;
  workerHeartbeat: {
    live: boolean;
    lastSeenAt: string | null;
    secondsAgo: number | null;
  };
  workerStatus: {
    pid: number | null;
    uptimeSeconds: number | null;
    recentCrashCount: number;
    crashLooping: boolean;
    crashedFlagSet: boolean;
  };
  asOf: string;
}

export interface RedisHealthData {
  connectionCount: number | null;
  connectionLimit: number;
  pctUsed: number | null;
  warning: boolean;
  threshold: number;
  redisAvailable: boolean;
  queueEnabled: boolean;
  queueDepths: {
    floorplan: number;
    bankStatement: number;
    taxReturn: number;
    assessmentRefresh: number;
  };
  asOf: string;
}

export interface SystemHealthData {
  status: "ok" | "error";
  env: string;
  timestamp: string;
  message?: string;
  queue: {
    enabled: boolean;
    redisReachable: boolean;
    queueDepths: {
      floorplan: number;
      bankStatement: number;
      taxReturn: number;
      assessmentRefresh: number;
    };
  };
  dbPool?: {
    total: number;
    idle: number;
    waiting: number;
    max: number;
  } | null;
}

export function QueueSparkline({ data, highThreshold = 5 }: { data: number[]; highThreshold?: number }) {
  const BAR_WIDTH = 3;
  const GAP = 1;
  const HEIGHT = 28;
  const max = Math.max(...data, highThreshold + 1, 1);
  const width = data.length * (BAR_WIDTH + GAP) - GAP;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  function getHourLabel(barIndex: number, total: number): string {
    const hoursAgo = total - 1 - barIndex;
    const now = new Date();
    const hour = new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
    const h = hour.getHours();
    const label = h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;
    return label;
  }

  const tooltipValue = hoveredIndex !== null ? data[hoveredIndex] : null;
  const tooltipLabel = hoveredIndex !== null ? getHourLabel(hoveredIndex, data.length) : null;
  const tooltipX = hoveredIndex !== null ? hoveredIndex * (BAR_WIDTH + GAP) + BAR_WIDTH / 2 : 0;

  return (
    <div className="relative shrink-0" style={{ width, height: HEIGHT }}>
      <svg
        width={width}
        height={HEIGHT}
        aria-label={`24-hour trend: ${data.join(", ")} renewals per hour`}
        className="block"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {data.map((value, i) => {
          const barHeight = Math.max(2, Math.round((value / max) * HEIGHT));
          const isHigh = value > highThreshold;
          return (
            <rect
              key={i}
              x={i * (BAR_WIDTH + GAP)}
              y={HEIGHT - barHeight}
              width={BAR_WIDTH}
              height={barHeight}
              rx="0.5"
              className={isHigh ? "fill-yellow-500 dark:fill-yellow-400" : "fill-primary/40"}
              onMouseEnter={() => setHoveredIndex(i)}
              style={{ cursor: "default" }}
            />
          );
        })}
      </svg>
      {hoveredIndex !== null && tooltipLabel !== null && tooltipValue !== null && (
        <div
          className="pointer-events-none absolute z-50 -translate-x-1/2 -translate-y-full rounded bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md border border-border whitespace-nowrap"
          style={{ left: tooltipX, top: -4 }}
          data-testid="sparkline-tooltip"
        >
          {tooltipLabel} — {tooltipValue} {tooltipValue === 1 ? "renewal" : "renewals"}
        </div>
      )}
    </div>
  );
}

export const AVAILABLE_PERMISSIONS: { key: string; label: string }[] = [
  { key: "view_dealers", label: "View Dealers" },
  { key: "manage_dealers", label: "Manage Dealers" },
  { key: "view_salespeople", label: "View Salespeople" },
  { key: "manage_salespeople", label: "Manage Salespeople" },
  { key: "view_commissions", label: "View Commissions" },
  { key: "manage_pricing", label: "Manage Pricing" },
  { key: "view_analytics", label: "View Analytics" },
  { key: "manage_rbac", label: "Manage RBAC" },
  { key: "manage_vendor_directory", label: "Manage Vendor Directory" },
  { key: "manage_messaging", label: "Manage Messaging" },
  { key: "manage_billing", label: "Manage Billing" },
  { key: "teamLeader", label: "Team Leader (scoped company view)" },
];

// Dashboard permissions for salesperson-role users
export const DASHBOARD_PERMISSIONS: { key: string; label: string; description: string }[] = [
  {
    key: "dashboard_salesperson",
    label: "Salesperson Dashboard",
    description: "Standard rep view — dealer list, own KPIs, own commissions",
  },
  {
    key: "dashboard_team",
    label: "Team Dashboard",
    description: "Sales Leader team performance view, team member KPIs, team commission track",
  },
  {
    key: "admin_salespeople",
    label: "Admin: Salespeople",
    description: "View and manage the Salespeople tab in admin",
  },
  {
    key: "admin_sales_teams",
    label: "Admin: Sales Teams",
    description: "View and manage the Sales Teams tab in admin",
  },
  {
    key: "admin_company_performance",
    label: "Admin: Company Performance",
    description: "View the company-wide performance and leaderboard tab",
  },
  {
    key: "admin_commissions",
    label: "Admin: Commissions",
    description: "View and action the commissions/payout tab",
  },
];

export function fmt(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function fmtRelative(d: string | null | undefined) {
  if (!d) return "Never";
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return fmt(d);
}

export function subBadge(status: string | null | undefined) {
  if (status === "active") return <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400">Active</Badge>;
  if (status === "trialing") return <Badge className="bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400">Trialing</Badge>;
  if (status === "canceling") return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400">Canceling</Badge>;
  if (status === "canceled") return <Badge variant="outline" className="text-muted-foreground">Canceled</Badge>;
  return <Badge variant="outline" className="text-muted-foreground">—</Badge>;
}

export function accountStatusBadge(status: string | null | undefined) {
  const s = status ?? "active";
  if (s === "active") return <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400">Active</Badge>;
  if (s === "on_leave") return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400">On Leave</Badge>;
  if (s === "suspended") return <Badge className="bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 flex items-center gap-1"><Lock className="w-3 h-3" />Suspended (NSF)</Badge>;
  if (s === "collections") return <Badge className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400">Collections</Badge>;
  if (s === "terminated") return <Badge className="bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-400">Terminated</Badge>;
  return <Badge variant="outline" className="text-muted-foreground">—</Badge>;
}

export function adminFetch(path: string, secret: string, opts: RequestInit = {}) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (secret) headers["Authorization"] = `Bearer ${secret}`;
  return fetch(path, { ...opts, headers: { ...headers, ...(opts.headers ?? {}) }, credentials: "same-origin" });
}

export function engagementScore(eventCount: number, uploadCount: number, lastActive: string | null): number {
  let score = 0;
  score += Math.min(eventCount * 2, 50);
  score += Math.min(uploadCount * 10, 30);
  if (lastActive) {
    const daysAgo = (Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24);
    if (daysAgo <= 3) score += 20;
    else if (daysAgo <= 7) score += 15;
    else if (daysAgo <= 14) score += 8;
    else if (daysAgo <= 30) score += 3;
  }
  return Math.min(score, 100);
}

export function activityBadge(score: number, lastActive: string | null) {
  const daysAgo = lastActive ? (Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24) : 999;
  if (daysAgo > 30 || score < 5) return <Badge variant="outline" className="text-xs text-gray-500 border-gray-300">Dormant</Badge>;
  if (daysAgo > 14 || score < 25) return <Badge className="text-xs bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400">At Risk</Badge>;
  if (score < 50) return <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400">Low Activity</Badge>;
  if (score < 75) return <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400">Active</Badge>;
  return <Badge className="text-xs bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400">Engaged</Badge>;
}

export function permissionsToChecked(permissionsJson: string): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  try {
    const parsed = JSON.parse(permissionsJson);
    for (const k of AVAILABLE_PERMISSIONS) {
      result[k.key] = !!parsed[k.key];
    }
  } catch {
    for (const k of AVAILABLE_PERMISSIONS) result[k.key] = false;
  }
  return result;
}

export function checkedToPermissionsJson(checked: Record<string, boolean>): string {
  const obj: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(checked)) {
    if (v) obj[k] = true;
  }
  return JSON.stringify(obj);
}

// ─── Pagination Utilities ─────────────────────────────────────────────────────

export const PAGE_SIZE_OPTIONS = [
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "All", value: 0 },
];
export const DEFAULT_PAGE_SIZE = 25;

export function usePagination<T>(items: T[], deps: unknown[] = []) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, deps);

  const totalItems = items.length;
  const totalPages = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);

  // Clamp page to valid range whenever items or pageSize change
  const clampedPage = Math.min(page, Math.max(0, totalPages - 1));

  const paged = pageSize === 0 ? items : items.slice(clampedPage * pageSize, (clampedPage + 1) * pageSize);

  function setPageSizeAndReset(size: number) {
    setPageSize(size);
    setPage(0);
  }

  return { page: clampedPage, setPage, pageSize, setPageSize: setPageSizeAndReset, totalPages, paged, totalItems };
}

export function PaginationControls({
  page,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  testId = "pagination",
}: {
  page: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (size: number) => void;
  testId?: string;
}) {
  const start = pageSize === 0 ? 1 : page * pageSize + 1;
  const end = pageSize === 0 ? totalItems : Math.min((page + 1) * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between mt-3 gap-2 flex-wrap text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <span>Show</span>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="h-7 text-xs w-20" data-testid={`${testId}-page-size`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span>per page</span>
      </div>
      <div className="flex items-center gap-2">
        {totalItems > 0 && (
          <span data-testid={`${testId}-info`}>
            {start}–{end} of {totalItems}
            {totalPages > 1 && <> · Page {page + 1} of {totalPages}</>}
          </span>
        )}
        <Button
          size="sm" variant="outline" className="h-7 text-xs px-2"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="w-3 h-3" />
        </Button>
        <Button
          size="sm" variant="outline" className="h-7 text-xs px-2"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
          data-testid={`${testId}-next`}
        >
          <ChevronRight className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
